import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ImageZoomModal.css';

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

export default function ImageZoomModal({
  isOpen,
  images,
  initialIndex,
  productTitle,
  onClose,
  onIndexChange,
}) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);

  const [index, setIndex] = useState(initialIndex || 0);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });

  const pointersRef = useRef(new Map());
  const gestureRef = useRef({
    mode: 'none',
    startX: 0,
    startY: 0,
    startTranslateX: 0,
    startTranslateY: 0,
    startDist: 0,
    startScale: 1,
    startMid: { x: 0, y: 0 },
  });

  const currentSrc = useMemo(() => (images && images.length ? images[index] : null), [images, index]);

  useEffect(() => {
    if (!isOpen) return;
    setIndex(initialIndex || 0);
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, [isOpen, initialIndex]);

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose && onClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, index, images]);

  const setIndexSafe = (next) => {
    if (!images || images.length === 0) return;
    const safe = (next + images.length) % images.length;
    setIndex(safe);
    onIndexChange && onIndexChange(safe);
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  };

  const handlePrev = () => {
    if (!images || images.length <= 1) return;
    setIndexSafe(index - 1);
  };

  const handleNext = () => {
    if (!images || images.length <= 1) return;
    setIndexSafe(index + 1);
  };

  const zoomTo = (nextScale, originX, originY) => {
    const container = containerRef.current;
    if (!container) {
      setScale(nextScale);
      return;
    }

    const rect = container.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;

    const ox = originX ?? cx;
    const oy = originY ?? cy;

    const relX = ox - cx;
    const relY = oy - cy;

    const ratio = nextScale / scale;

    setTranslate((t) => ({
      x: t.x + relX * (1 - ratio),
      y: t.y + relY * (1 - ratio),
    }));

    setScale(nextScale);
  };

  const onWheel = (e) => {
    e.preventDefault();
    if (!isOpen) return;

    const delta = -e.deltaY;
    const factor = delta > 0 ? 1.08 : 0.92;
    const nextScale = clamp(scale * factor, 1, 4);
    zoomTo(nextScale, e.clientX, e.clientY);
  };

  const onPointerDown = (e) => {
    // Don't hijack pointer events intended for UI controls
    try {
      const target = e.target;
      if (target && target.closest && target.closest('.image-zoom-close, .image-zoom-nav')) {
        return;
      }
    } catch (_) {}

    const el = containerRef.current;
    if (!el) return;

    el.setPointerCapture && el.setPointerCapture(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    const pts = Array.from(pointersRef.current.values());

    if (pts.length === 1) {
      gestureRef.current.mode = 'pan';
      gestureRef.current.startX = e.clientX;
      gestureRef.current.startY = e.clientY;
      gestureRef.current.startTranslateX = translate.x;
      gestureRef.current.startTranslateY = translate.y;
    } else if (pts.length === 2) {
      const [p1, p2] = pts;
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const dist = Math.hypot(dx, dy);
      gestureRef.current.mode = 'pinch';
      gestureRef.current.startDist = dist;
      gestureRef.current.startScale = scale;
      gestureRef.current.startTranslateX = translate.x;
      gestureRef.current.startTranslateY = translate.y;
      gestureRef.current.startMid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    }
  };

  const onPointerMove = (e) => {
    if (!pointersRef.current.has(e.pointerId)) return;

    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = Array.from(pointersRef.current.values());

    if (gestureRef.current.mode === 'pan' && pts.length === 1) {
      if (scale <= 1) return;
      const dx = e.clientX - gestureRef.current.startX;
      const dy = e.clientY - gestureRef.current.startY;
      setTranslate({
        x: gestureRef.current.startTranslateX + dx,
        y: gestureRef.current.startTranslateY + dy,
      });
      return;
    }

    if (pts.length === 2) {
      const [p1, p2] = pts;
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const dist = Math.hypot(dx, dy);
      const nextScale = clamp((gestureRef.current.startScale * dist) / (gestureRef.current.startDist || 1), 1, 4);

      const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
      const startMid = gestureRef.current.startMid;

      const moveDx = mid.x - startMid.x;
      const moveDy = mid.y - startMid.y;

      const container = containerRef.current;
      const rect = container ? container.getBoundingClientRect() : null;
      const cx = rect ? rect.left + rect.width / 2 : mid.x;
      const cy = rect ? rect.top + rect.height / 2 : mid.y;

      const relX = startMid.x - cx;
      const relY = startMid.y - cy;
      const ratio = nextScale / (gestureRef.current.startScale || 1);

      setTranslate({
        x: gestureRef.current.startTranslateX + moveDx + relX * (1 - ratio),
        y: gestureRef.current.startTranslateY + moveDy + relY * (1 - ratio),
      });
      setScale(nextScale);
      gestureRef.current.mode = 'pinch';
    }
  };

  const onPointerUp = (e) => {
    pointersRef.current.delete(e.pointerId);

    const pts = Array.from(pointersRef.current.values());
    if (pts.length === 0) {
      gestureRef.current.mode = 'none';
      if (scale <= 1) {
        setTranslate({ x: 0, y: 0 });
      }
    } else if (pts.length === 1) {
      const p = pts[0];
      gestureRef.current.mode = 'pan';
      gestureRef.current.startX = p.x;
      gestureRef.current.startY = p.y;
      gestureRef.current.startTranslateX = translate.x;
      gestureRef.current.startTranslateY = translate.y;
    }
  };

  const toggleZoom = (e) => {
    if (!isOpen) return;
    if (scale === 1) {
      zoomTo(2, e.clientX, e.clientY);
    } else {
      setScale(1);
      setTranslate({ x: 0, y: 0 });
    }
  };

  if (!isOpen) return null;

  const modal = (
    <div className="image-zoom-modal" role="dialog" aria-modal="true">
      <div className="image-zoom-backdrop" onClick={onClose} />

      <div
        className="image-zoom-content"
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={toggleZoom}
      >
        <button className="image-zoom-close" onClick={onClose} aria-label="Close">
          ✕
        </button>

        {images && images.length > 1 && (
          <>
            <button className="image-zoom-nav image-zoom-prev" onClick={handlePrev} aria-label="Previous image">
              <i className="fas fa-chevron-left" />
            </button>
            <button className="image-zoom-nav image-zoom-next" onClick={handleNext} aria-label="Next image">
              <i className="fas fa-chevron-right" />
            </button>
          </>
        )}

        <div className="image-zoom-counter">
          {images && images.length ? `${index + 1} / ${images.length}` : ''}
        </div>

        <div className="image-zoom-stage">
          {currentSrc && (
            <img
              ref={imgRef}
              src={currentSrc}
              alt={`${productTitle || 'Product'} - Image ${index + 1}`}
              className="image-zoom-img"
              style={{
                transform: `translate3d(${translate.x}px, ${translate.y}px, 0) scale(${scale})`,
              }}
              draggable={false}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>

        <div className="image-zoom-hint">
          Pinch / Scroll to zoom • Drag to move • Double tap to zoom
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
